import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Radio, Trophy, Zap, X, Flag, Share2 } from "lucide-react";

const MAX_MISTAKES = 4;
const TIRE_ORDER = ["tr", "br", "bl", "tl"];

function Tire({ pos, lit, clickable, onClick, showHint }) {
  const place = { tl: "-left-[6px] top-14", tr: "-right-[6px] top-14", bl: "-left-[6px] bottom-16", br: "-right-[6px] bottom-16" }[pos];
  return (
    <div className={`absolute ${place} z-20 flex items-center`}>
      {pos === "tr" && lit && showHint && (
        <span className="absolute right-7 -top-6 whitespace-nowrap text-[9px] font-bold uppercase tracking-wide bg-white text-neutral-900 border border-neutral-900 rounded px-1.5 py-0.5">Tap to restart</span>
      )}
      <button type="button" onClick={clickable ? onClick : undefined} disabled={!clickable} data-testid={`tire-${pos}`} aria-pressed={lit}
        className={`w-[13px] h-[54px] rounded-md transition-all duration-200 flex items-center justify-center ${lit ? "bg-race-red ring-2 ring-white" : "bg-neutral-900"} ${clickable ? "cursor-pointer" : "cursor-default"}`}
        style={lit ? { boxShadow: "0 0 14px rgba(255,42,0,0.85)" } : { boxShadow: "none" }}>
        {lit && <X size={9} className="text-white" strokeWidth={3} />}
      </button>
    </div>
  );
}
import api, { apiError } from "@/lib/api";
import RacingKeyboard from "@/components/RacingKeyboard";
import RacePositions from "@/components/RacePositions";
import { CountdownTakeover, TAKEOVER_TOP, TAKEOVER_BOTTOM } from "@/components/CountdownTakeover";
import { Speedometer } from "@/components/Speedometer";
import { tokenizeTarget, charsDone } from "@/lib/raceTokens";
import { iosHaptic } from "@/lib/haptics";
import { hapticsEnabled } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { SponsorAd } from "@/components/SponsorAd";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const adSrc = (u) => (u ? `${BACKEND}${u}` : null);
const avatarSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

function haptic(ms) {
  if (!hapticsEnabled()) return;
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try { if (navigator.vibrate(ms)) return; } catch (e) { /* fall through */ }
  }
  iosHaptic();
}

export default function Lobby() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myIdRef = useRef(null);
  myIdRef.current = user?.id || null;

  const [phase, setPhase] = useState("connecting"); // connecting|lobby|countdown|racing|results
  const [roster, setRoster] = useState([]);
  const [contest, setContest] = useState(null);
  const [startEpoch, setStartEpoch] = useState(null);
  const [count, setCount] = useState(10);
  const [race, setRace] = useState(null);
  const [pos, setPos] = useState(0);
  const [streak, setStreak] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [dqt, setDqt] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  const [flash, setFlash] = useState(0);
  const [opponents, setOpponents] = useState({}); // user_id -> {name,pos,cps}
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState([]);
  const [lobbyLeft, setLobbyLeft] = useState(null);
  const [featureAd, setFeatureAd] = useState(null);   // countdown takeover, fetched once at the Start-Line

  const wsRef = useRef(null);
  const closingRef = useRef(false);
  const reconnectRef = useRef(null);
  const phaseRef = useRef("connecting");
  const [confirmExit, setConfirmExit] = useState(false);
  const offsetRef = useRef(0);          // serverNow - clientNow
  const goRef = useRef(null);           // performance-based race start
  const startedSessionRef = useRef(null);
  const seqRef = useRef(0); // guards duplicate cross-pod race_start
  const ksRef = useRef([]);
  const posRef = useRef(0);
  const penalizedRef = useRef(new Set());
  const rafRef = useRef(null);
  const lastProgRef = useRef(0);
  const finishedRef = useRef(false);

  const tokens = race ? tokenizeTarget(race.target || "") : [];

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => {   // fetched + preloaded off-screen; only shown during the final countdown
    // Shape guard: only a true 1080x2340 phone-shaped creative may take over the screen;
    // older 4:5 or mis-sized images fall back to banner + branded backdrop (never cropped).
    api.get("/ads/serve?placement=countdown_feature").then((r) => {
      const url = r.data?.ad?.image_url;
      if (!url) return setFeatureAd({ ad: null });
      const im = new Image();
      im.onload = () => setFeatureAd(Math.abs(im.naturalWidth / im.naturalHeight - 1080 / 2340) <= 0.04 ? r.data : { ad: null });
      im.onerror = () => setFeatureAd({ ad: null });
      im.src = url;
    }).catch(() => setFeatureAd({ ad: null }));
  }, [id]);
  const hasTakeover = !!featureAd?.ad?.image_url;
  // Takeover safe zones: the race card + Live Positions must sit inside TAKEOVER_TOP..TAKEOVER_BOTTOM
  // of the screen on every phone (scaled down on short screens), so logo + message stay visible.
  const bandRef = useRef(null);
  const [bandScale, setBandScale] = useState(1);
  useLayoutEffect(() => {
    if (!(phase === "countdown" && hasTakeover) || !bandRef.current) { setBandScale(1); return undefined; }
    const fit = () => {
      const el = bandRef.current;
      if (!el) return;
      const h = el.offsetHeight;
      const room = TAKEOVER_BOTTOM * window.innerHeight - (el.getBoundingClientRect().top + window.scrollY);
      setBandScale(h > room ? Math.max(0.6, room / h) : 1);
    };
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(bandRef.current);
    window.addEventListener("resize", fit);
    return () => { ro?.disconnect(); window.removeEventListener("resize", fit); };
  }, [phase, hasTakeover]);
  // Takeover is always dark (status-bar tint, page edges, Live Positions) like the reference.
  useEffect(() => {
    const on = phase === "countdown" && hasTakeover;
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = meta?.getAttribute("content");
    document.documentElement.classList.toggle("force-dark", on);
    if (on && meta) meta.setAttribute("content", "#000000");
    return () => { document.documentElement.classList.remove("force-dark"); if (meta && prev) meta.setAttribute("content", prev); };
  }, [phase, hasTakeover]);

  // Immersive race mode: hide global header/footer during countdown + racing.
  useEffect(() => {
    const on = phase === "countdown" || phase === "racing";
    document.body.classList.toggle("immersive-race", on);
    return () => document.body.classList.remove("immersive-race");
  }, [phase]);

  // ---- WebSocket (with auto-reconnect so a blip/navigation never drops you) ----
  useEffect(() => {
    api.get(`/contests/${id}`).then((r) => {
      setContest(r.data);
      // A contest that's already been raced is closed - go straight to Final Results.
      if (r.data?.status === "completed") { navigate(`/results/${id}`, { replace: true }); return; }
    }).catch(() => {});
    const connect = () => {
      const ws = new WebSocket(`${WS_BASE}/api/ws/contest/${id}`);
      wsRef.current = ws;
      ws.onopen = () => setPhase((p) => (p === "connecting" ? "lobby" : p));
      ws.onmessage = (ev) => handleMsg(JSON.parse(ev.data));
      ws.onclose = () => {
        // Reconnect quietly while the race is still relevant (keeps your grid spot).
        if (closingRef.current) return;
        if (phaseRef.current === "results") return;
        reconnectRef.current = setTimeout(connect, 1500);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      closingRef.current = true;
      clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line
  }, [id]);

  const send = (obj) => { try { wsRef.current?.send(JSON.stringify(obj)); } catch {} };

  const exitStartLine = () => {
    closingRef.current = true;
    clearTimeout(reconnectRef.current);
    send({ type: "exit" });
    try { wsRef.current?.close(); } catch {}
    navigate("/calendar");
  };

  const shareRace = async () => {
    const url = `${window.location.origin}/contests/${id}`;
    const title = contest?.name ? `${contest.name} · Text Racing` : "Text Racing";
    const text = contest?.name
      ? `I'm at the Start-Line for "${contest.name}" on Text Racing. Jump in and race me!`
      : "Join me at the Start-Line on Text Racing!";
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch { return; }   // user cancelled the native sheet
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Race link copied", { description: "Share it so friends can join you at the Start-Line." });
    } catch {
      toast.message("Share this race", { description: url });
    }
  };

  const handleMsg = (m) => {
    if (m.type === "error") { toast.error(m.detail); return; }
    if (m.type === "state") {
      offsetRef.current = m.server_now - Date.now();
      setRoster(m.roster || []);
      setStartEpoch(m.start_epoch);
      if (m.race && (m.phase === "racing" || m.phase === "countdown")) beginRace(m.race, m.go_epoch || (Date.now() + offsetRef.current + 4000));
      if (m.resume) { seqRef.current = m.resume.seq || 0; posRef.current = m.resume.pos || 0; setPos(m.resume.pos || 0); }
      setPhase((p) => (m.phase === "racing" || m.phase === "countdown" ? p : "lobby"));
    } else if (m.type === "presence") {
      setRoster(m.roster || []);
    } else if (m.type === "race_start") {
      setRoster(m.roster || []);
      beginRace(m.race, m.go_epoch);
    } else if (m.type === "go") {
      // T-0: the server releases the real race content only now.
      if (m.race && m.race.race_session_id === startedSessionRef.current) setRace(m.race);
      else if (m.race) beginRace(m.race, m.go_epoch);
    } else if (m.type === "ping") {
      send({ type: "pong", id: m.id, ts: m.ts });
    } else if (m.type === "resync") {
      seqRef.current = m.seq; posRef.current = m.pos; setPos(m.pos);
    } else if (m.type === "positions") {
      setOpponents((o) => {
        const next = { ...o };
        for (const r of m.racers || []) {
          if (r.user_id !== myIdRef.current) next[r.user_id] = { name: r.name, pos: r.pos, cps: r.cps, avatar: r.avatar };
        }
        return next;
      });
    } else if (m.type === "finished") {
      if (m.user_id === myIdRef.current) {
        setResult({ final_time: m.final_time, cps: m.cps, errors: m.errors, accuracy: m.accuracy, dqt: m.dqt, placement: m.place, timing: "server" });
        setPhase("results");
      }
      setBoard((b) => {
        if (b.find((x) => x.user_id === m.user_id)) return b;
        return [...b, { user_id: m.user_id, name: m.name, avatar: m.avatar, final_time: m.final_time, cps: m.cps, place: m.place }];
      });
    } else if (m.type === "final") {
      setBoard(m.leaderboard || []);
    }
  };

  // ---- race start (synchronized) ----
  const beginRace = (r, goEpoch) => {
    // Multiple pods can each emit a race_start (relayed via Redis); process only
    // the first for a given session so we never reset the clock mid-race.
    if (startedSessionRef.current === r.race_session_id) return;
    startedSessionRef.current = r.race_session_id;
    setRace(r.masked ? { ...r, target: r.placeholder } : r);
    ksRef.current = []; posRef.current = 0; seqRef.current = 0; penalizedRef.current = new Set();
    setPos(0); setStreak(0); setMistakes(0); setDqt(false);
    finishedRef.current = false;
    setPhase("countdown");
    const tick = () => {
      const msLeft = goEpoch - (Date.now() + offsetRef.current);
      if (msLeft <= 0) {
        goRef.current = performance.now();
        setPhase("racing");
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setCount(Math.min(10, Math.max(1, Math.ceil(msLeft / 1000))));
        setTimeout(tick, 100);
      }
    };
    tick();
  };

  const loop = useCallback(() => {
    if (goRef.current == null) return;
    setElapsed((performance.now() - goRef.current) / 1000);
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  // ---- lobby countdown label ----
  useEffect(() => {
    if (phase !== "lobby" || !startEpoch) return;
    const iv = setInterval(() => {
      const s = Math.max(0, Math.round((startEpoch - (Date.now() + offsetRef.current)) / 1000));
      setLobbyLeft(s);
    }, 250);
    return () => clearInterval(iv);
  }, [phase, startEpoch]);

  // ---- authoritative, cross-pod results --------------------------------------
  // The live WS "final"/"finished" board only contains racers on THIS backend pod.
  // Once we reach the results screen, pull the official DB-scored leaderboard so
  // EVERY racer appears with their true server-scored time, penalties and placement
  // - regardless of which pod they finished on. Poll briefly to catch late finishers.
  useEffect(() => {
    if (phase !== "results") return;
    let stop = false;
    let tries = 0;
    const pull = async () => {
      try {
        const { data } = await api.get(`/contests/${id}/results`);
        const rows = data?.results || [];
        if (!stop && rows.length) {
          setBoard((prev) => {
            const prizeByUid = {};
            prev.forEach((p) => { if (p.prize) prizeByUid[p.user_id] = p.prize; });
            return rows.map((r) => ({
              user_id: r.user_id, name: r.name, avatar: r.avatar,
              final_time: r.final_time, cps: r.cps, place: r.place,
              penalty_time: r.penalty_time, errors: r.errors,
              dqt: !!r.dqt, dnf: false,
              prize: prizeByUid[r.user_id] || 0,
            }));
          });
        }
      } catch { /* keep the WS board until the official one lands */ }
      tries += 1;
      if (!stop && tries < 8) setTimeout(pull, 2000);
    };
    pull();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [phase, id]);


  // ---- typing ----
  const finish = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    // Official time is measured by the server (key arrival); this is only a consistency hint.
    send({ type: "finished", client_elapsed_ms: Math.round(performance.now() - goRef.current) });
  }, []);

  const press = useCallback((key) => {
    if (phase !== "racing" || race?.masked) return;
    const value = key === "space" ? " " : key;
    const t = performance.now() - goRef.current;
    ksRef.current.push({ v: value, t });
    seqRef.current += 1;
    send({ type: "key", seq: seqRef.current, v: value });   // server timestamps + validates; UI stays instant

    const tks = tokenizeTarget(race.target || "");
    const cur = posRef.current;
    const expected = tks[cur];
    let np = cur;
    let correct = false;
    if (expected != null && value.toLowerCase() === expected.toLowerCase()) {
      // correct → advance cursor, extend the accurate streak
      correct = true;
      np = cur + 1;
      posRef.current = np;
      setPos(np);
      setStreak((s) => s + expected.length);
      haptic(12);
    } else {
      // miss → cursor HOLDS; light one tire (single penalty per position); reset glow
      setStreak(0);
      setFlash((f) => f + 1);
      haptic([50, 30, 50]);
      if (!penalizedRef.current.has(cur)) {
        penalizedRef.current.add(cur);
        setMistakes((mn) => { const nm = mn + 1; if (nm >= MAX_MISTAKES) setDqt(true); return nm; });
      }
    }

    // throttle live-position broadcast (based on characters traversed)
    const doneChars = charsDone(tks, np);
    const totalChars = (race.target || "").length || 1;
    const progress = Math.min(1, doneChars / totalChars);
    const nowp = performance.now();
    if (nowp - lastProgRef.current > 90 || np >= tks.length) {
      lastProgRef.current = nowp;   // opponents' positions now come from the server's validated cursor
      void progress;
    }
    if (np >= tks.length) finish();
    return correct;
  }, [phase, race, finish]);

  // ---- 4-tire penalty + restart (clock keeps running) ----
  const handleRestart = () => {
    setShowRestart(false);
    ksRef.current = [];
    posRef.current = 0;
    penalizedRef.current = new Set();
    setPos(0); setStreak(0); setMistakes(0); setDqt(false);
    send({ type: "restart" });
    haptic(20); // goRef (race clock) intentionally preserved - time keeps counting
  };
  const openRestart = () => { if (!dqt) setShowRestart(true); };
  const tireLit = (p) => mistakes >= TIRE_ORDER.indexOf(p) + 1;

  // ---- derived race view ----
  const guide = race?.race_type === "sprint";
  const isMissed = (i) => penalizedRef.current.has(i);
  const doneChars = race ? charsDone(tokens, pos) : 0;
  const penaltySecs = race?.penalty_seconds || 0;
  const liveCps = race && elapsed > 0.2 ? +(doneChars / (elapsed + mistakes * penaltySecs)).toFixed(2) : 0;

  const meProg = race ? Math.min(1, doneChars / ((race.target || "").length || 1)) : 0;
  const racers = race ? [
    { name: "YOU", progress: meProg, isPlayer: true, tier: "player", avatar: user?.profile_image_url },
    ...Object.entries(opponents).map(([, o]) => ({ name: o.name, progress: o.pos, tier: "pro", avatar: o.avatar })),
    ...(race.ghosts || []).map((g) => ({
      name: g.name, tier: g.tier,
      progress: Math.min(1, elapsed / (((race.target || "").length / g.cps) || 1)),
    })),
  ] : [];

  // ---- sponsorship timing (Race Clock) ----
  // Pre-Race shows until T-1:30; In-Race from T-1:29 through the race; Post-Race on results.
  const preAd = adSrc(contest?.pre_race_ad_url);
  const inAd = adSrc(contest?.in_race_ad_url);
  const postAd = adSrc(contest?.post_race_ad_url);
  const inFinalWindow = lobbyLeft != null && lobbyLeft <= 90;

  // ============ RENDER ============
  if (phase === "connecting")
    return <div className="text-center text-race-sub py-20">Pulling up to the Start-Line…</div>;

  if (phase === "lobby")
    return (
      <div className="max-w-lg mx-auto animate-slide-up" data-testid="lobby-page">
        <div className="flex items-center gap-2 text-race-red mb-2"><Radio size={16} className="animate-pulse" /><span className="overline">Start-Line</span></div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">{contest?.name || "Contest"}</h1>
        <p className="text-race-sub mt-1 capitalize">{contest?.race_type} · starts at the scheduled time</p>

        <div className="mt-6 bg-race-surface border border-race-line rounded-lg p-6 text-center">
          <div className="overline mb-2">Green flag in</div>
          <div className="tabular font-heading text-6xl" data-testid="lobby-countdown">
            {lobbyLeft == null ? "-" : lobbyLeft > 0 ? `${Math.floor(lobbyLeft / 60)}:${String(lobbyLeft % 60).padStart(2, "0")}` : "GO"}
          </div>
          <p className="text-xs text-race-muted mt-3">Stay on this screen or come back anytime - you keep your spot at the Start-Line until you exit.</p>
        </div>

        {/* Pre-Race sponsor - visible until T-1:30 */}
        {!inFinalWindow && preAd && (
          <div className="mt-6" data-testid="pre-race-ad">
            <div className="overline mb-2 text-race-sub">Sponsored</div>
            <div className="mx-auto bg-white/95 border border-race-line rounded-md overflow-hidden"
                 style={{ aspectRatio: contest?.pre_race_ad_format === "double" ? "324 / 132" : "324 / 77",
                          maxWidth: 324 }}>
              <img src={preAd} alt="Pre-race sponsor" className="w-full h-full object-contain" />
            </div>
          </div>
        )}
        {/* In-Race sponsor takes over inside the final 1:30 */}
        {inFinalWindow && inAd && (
          <div className="mt-6" data-testid="in-race-ad-lobby">
            <div className="overline mb-2 text-race-sub">Sponsored</div>
            <img src={inAd} alt="Sponsor" className="w-full rounded-md border border-race-line" />
          </div>
        )}
        {/* Network sponsor fallback when the contest has no banner for the active slot.
            In-race network ad now shows throughout the lobby (not only the final 1:30)
            whenever the contest has no in-race banner of its own. */}
        {!inFinalWindow && !preAd && (
          <SponsorAd placement={inAd ? "pregame" : "in_race"} ambient={!inAd} className="mt-6" />
        )}
        {inFinalWindow && !inAd && <SponsorAd placement="in_race" ambient className="mt-6" />}

        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3"><Users size={16} /><span className="overline">At the Start-Line - {roster.length}</span></div>
          <div className="space-y-2" data-testid="lobby-roster">
            {roster.map((r) => (
              <div key={r.user_id} className="flex items-center gap-3 bg-race-elevated border border-race-line rounded-md px-4 py-2">
                <span className={`w-2 h-2 rounded-full ${r.connected === false ? "bg-race-muted" : "bg-race-red animate-pulse"}`} />
                <span className="w-7 h-7 rounded-full overflow-hidden shrink-0 bg-race-red text-race-bg flex items-center justify-center font-heading text-xs" data-testid={`roster-avatar-${r.user_id}`}>
                  {r.avatar
                    ? <img src={avatarSrc(r.avatar)} alt="" className="w-full h-full object-cover" />
                    : (r.name || "R").slice(0, 1).toUpperCase()}
                </span>
                <span className="font-medium text-race-txt flex-1">{r.name}{r.user_id === user?.id ? " (you)" : ""}</span>
                {r.connected === false && <span className="text-[10px] text-race-muted uppercase tracking-widest">away</span>}
              </div>
            ))}
            {roster.length === 0 && <p className="text-race-sub text-sm">Waiting for racers…</p>}
          </div>
          <p className="text-xs text-race-muted mt-3">Everyone launches together at the scheduled time. Racers who step away briefly stay on the grid.</p>
        </div>

        {!confirmExit ? (
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={shareRace} data-testid="share-race-btn"
              className="inline-flex items-center gap-2 border border-race-volt/60 text-race-volt hover:bg-race-volt/10 text-sm font-mono uppercase tracking-wide px-4 py-2.5 rounded-sm transition-colors">
              <Share2 size={15} /> Share Race
            </button>
            <button onClick={() => setConfirmExit(true)} data-testid="exit-start-line"
              className="border border-race-line text-race-sub hover:text-race-red hover:border-race-red text-sm font-mono uppercase tracking-wide px-4 py-2.5 rounded-sm transition-colors">
              Exit Start-Line
            </button>
          </div>
        ) : (
          <div className="mt-6 border border-race-red/50 bg-race-red/5 rounded-md p-4" data-testid="exit-confirm">
            <p className="text-sm text-race-txt">Leave the Start-Line? You'll forfeit your spot in this race and any entry fee is refunded per contest rules.</p>
            <div className="flex gap-2 mt-3">
              <button onClick={exitStartLine} data-testid="exit-confirm-yes"
                className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">Yes, exit</button>
              <button onClick={() => setConfirmExit(false)} data-testid="exit-confirm-no"
                className="border border-race-line text-race-sub hover:text-race-txt px-4 py-2.5 rounded-sm text-xs font-mono">Stay at the line</button>
            </div>
          </div>
        )}
      </div>
    );

  if (phase === "results")
    return (
      <div className="max-w-lg mx-auto animate-slide-up" data-testid="mp-results">
        <div className="flex items-center gap-2 text-race-red mb-2"><Trophy size={18} /><span className="overline">Photo finish</span></div>
        <h1 className="font-heading text-5xl tracking-tight">Results</h1>
        {(() => {
          const myWin = board.find((r) => r.user_id === user?.id && r.prize > 0);
          return myWin ? (
            <div className="mt-4 border border-race-volt bg-race-surface rounded-md p-4 flex items-center gap-3" data-testid="race-prize-won">
              <Trophy size={22} className="text-race-volt" />
              <div>
                <div className="overline text-race-sub">You won this race</div>
                <div className="tabular font-heading text-3xl text-race-volt">+{myWin.prize.toLocaleString()} credits</div>
              </div>
            </div>
          ) : null;
        })()}
        {postAd && (
          <img src={postAd} alt="Sponsor" data-testid="post-race-ad"
            className="w-full rounded-md border border-race-line my-5 object-cover" />
        )}
        {!postAd && <SponsorAd placement="post_race" className="my-5" />}
        {result && (
          <div className="grid grid-cols-3 gap-3 my-5 text-center">
            <Stat label="CPS" value={result.cps} />
            <Stat label="Accuracy" value={`${result.accuracy}%`} />
            <Stat label="Final" value={`${result.final_time}s`} />
          </div>
        )}
        <div className="overline mb-2">Final leaderboard</div>
        {(() => {
          const top = [...board].filter((r) => !r.dnf && !r.dqt).sort((a, b) => (a.place || 99) - (b.place || 99)).slice(0, 3);
          if (top.length < 2) return null;
          const order = top.length >= 3 ? [top[1], top[0], top[2]] : [top[1], top[0]];
          const podH = { 1: "h-20", 2: "h-14", 3: "h-10" };
          return (
            <div className="flex items-end justify-center gap-3 mb-5" data-testid="mp-podium">
              {order.map((r) => (
                <div key={r.user_id} className="flex flex-col items-center" style={{ width: 84 }}>
                  <div className={`rounded-full overflow-hidden mb-1 border-2 ${r.place === 1 ? "border-race-volt w-14 h-14" : "border-race-line w-11 h-11"} bg-race-red text-race-bg flex items-center justify-center font-heading`}>
                    {r.avatar ? <img src={avatarSrc(r.avatar)} alt="" className="w-full h-full object-cover" /> : (r.name || "R").slice(0, 1).toUpperCase()}
                  </div>
                  <span className="text-[11px] font-medium truncate max-w-full text-center">{r.user_id === user?.id ? "You" : r.name}</span>
                  <div className={`w-full ${podH[r.place]} mt-1 rounded-t-sm flex items-start justify-center pt-1 font-heading ${r.place === 1 ? "bg-race-volt/80 text-race-bg" : "bg-race-elevated text-race-sub"}`}>{r.place}</div>
                </div>
              ))}
            </div>
          );
        })()}
        <div className="space-y-2" data-testid="mp-leaderboard">
          {board.length === 0 && <p className="text-race-sub text-sm">Waiting for other racers to finish…</p>}
          {board.sort((a, b) => (a.place || 99) - (b.place || 99)).map((r) => (
            <div key={r.user_id} className="flex items-center gap-3 bg-race-surface border border-race-line rounded-md px-4 py-2">
              <span className="font-heading text-xl w-6">{r.dnf ? "-" : r.place}</span>
              <span className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-race-red text-race-bg flex items-center justify-center font-heading text-sm" data-testid={`mp-avatar-${r.user_id}`}>
                {r.avatar ? <img src={avatarSrc(r.avatar)} alt="" className="w-full h-full object-cover" /> : (r.name || "R").slice(0, 1).toUpperCase()}
              </span>
              <span className="font-medium flex-1">{r.name}{r.user_id === user?.id ? " (you)" : ""}</span>
              <span className="tabular text-race-sub text-sm">
                {r.dnf ? `DNF · ${r.cps} CPS` : `${r.final_time}s · ${r.cps} CPS`}
              </span>
            </div>
          ))}
        </div>
        <button onClick={() => navigate("/calendar")} data-testid="mp-exit"
          className="mt-6 bg-race-red hover:bg-race-redhover text-race-bg font-heading text-2xl tracking-wide px-8 py-3 rounded-sm transition-colors">
          Back to Calendar
        </button>
      </div>
    );

  // countdown + racing (vehicle)
  return (
    <div className="race-surface max-w-[430px] mx-auto pt-2" data-testid="mp-race">
      {flash > 0 && <div key={flash} className="fixed inset-0 pointer-events-none animate-error-pulse z-40" />}
      {/* Final-countdown = a sponsored event moment: small "presented by" branding
          up top, then the race card, Live Positions, and a large feature ad below. */}
      {phase === "countdown" && <CountdownTakeover ad={featureAd} />}
      {phase === "countdown" ? (
        hasTakeover ? <div style={{ height: `calc(${TAKEOVER_TOP * 100}dvh - 1rem)` }} data-testid="takeover-logo-zone" /> : (
          <div className="relative z-10 mx-3 mb-2" data-testid="countdown-banner">
            <SponsorAd placement="in_race" ambient noClick fallback={
              <div className="flex items-center justify-center gap-2 py-1" data-testid="presented-by">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-race-sub">Race presented by</span>
                <span className="font-heading text-sm tracking-tight text-race-txt">{contest?.sponsor_name || "Text Racing"}</span>
              </div>} />
          </div>
        )
      ) : inAd ? (
        <div className="mx-3 mb-2" data-testid="in-race-ad">
          <img src={inAd} alt="Sponsor" className="w-full max-h-14 object-contain opacity-80 rounded-md" />
        </div>
      ) : (
        <div className="mx-3 mb-2" data-testid="in-race-ad-network">
          <SponsorAd placement="in_race" ambient />
        </div>
      )}
      <div ref={bandRef} style={bandScale < 1 ? { transform: `scale(${bandScale})`, transformOrigin: "top center" } : undefined} data-testid="takeover-band">
      <div className="relative z-10 mx-3">
        <Tire pos="tr" lit={tireLit("tr")} clickable={phase === "racing" && tireLit("tr") && !dqt} onClick={openRestart} showHint={phase === "racing" && !dqt} />
        <Tire pos="br" lit={tireLit("br")} clickable={phase === "racing" && tireLit("br") && !dqt} onClick={openRestart} />
        <Tire pos="bl" lit={tireLit("bl")} clickable={phase === "racing" && tireLit("bl") && !dqt} onClick={openRestart} />
        <Tire pos="tl" lit={tireLit("tl")} clickable={false} onClick={openRestart} />
        <div className="relative bg-white text-neutral-900 rounded-2xl border border-neutral-300 shadow-[0_0_40px_rgba(0,0,0,0.6)] overflow-hidden">
          {showRestart && (
            <div className="absolute inset-0 z-50 bg-black/70 flex items-center justify-center rounded-2xl px-6" data-testid="restart-confirm">
              <div className="bg-white text-neutral-900 rounded-xl p-5 w-full max-w-[260px] text-center border border-neutral-300">
                <div className="font-heading text-3xl">RESTART?</div>
                <p className="text-xs text-neutral-600 mt-2 mb-4">Clears the track for a clean run. The race clock <b>keeps running</b> - you trade time for accuracy.</p>
                <div className="flex gap-2">
                  <button data-testid="restart-cancel-btn" onClick={() => setShowRestart(false)} className="flex-1 border border-neutral-300 hover:border-neutral-500 rounded-md py-2 text-sm font-medium">Cancel</button>
                  <button data-testid="restart-confirm-btn" onClick={handleRestart} className="flex-1 bg-race-red hover:bg-race-redhover text-white rounded-md py-2 text-sm font-semibold">Restart</button>
                </div>
              </div>
            </div>
          )}
          {dqt && phase === "racing" && (
            <div className="mx-6 mt-3 flex items-center gap-2 bg-race-red text-white text-[11px] font-bold uppercase tracking-wide rounded-md px-3 py-1.5" data-testid="dqt-banner">
              <Flag size={12} /> Time Disqualified - finish for character credit
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
            <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">{contest?.name}</span>
            <span className="tabular text-sm text-neutral-900">{elapsed.toFixed(2)}s</span>
          </div>

          <div className="px-6 py-3 min-h-[104px] flex flex-col justify-center">
            {!guide && phase === "racing" && <p className="font-body text-lg font-semibold mb-3" data-testid="mp-question">{race.prompt}</p>}
            <p className="font-mono text-xl md:text-2xl leading-relaxed break-words tracking-[0.08em]" data-testid="mp-target">
              {phase === "countdown"
                ? <span className="text-neutral-400">Get ready…</span>
                : tokens.map((tok, i) => {
                    const isDone = i < pos;
                    const isCur = i === pos;
                    const missed = isCur && isMissed(i);
                    let disp;
                    if (isDone || guide) disp = tok;
                    else disp = isCur ? "_" : "·";
                    let style = {};
                    let cls = "";
                    if (isDone) style = { color: "#16a34a", fontWeight: 700 };
                    else if (missed) { style = { background: "#dc2626", color: "#fff", fontWeight: 800 }; cls = "rounded px-1"; }
                    else if (isCur) { style = { background: "#111827", color: "#fff", fontWeight: 800 }; cls = "rounded px-1"; }
                    else if (guide) style = { color: "#111827", fontWeight: 600 };
                    else style = { color: "#cbd5e1" };
                    return <span key={i} className={`${cls} ${tok === ".com" ? "whitespace-nowrap" : ""}`} style={style}>{disp}</span>;
                  })}
            </p>
          </div>

          <div className="px-3 pb-2">
            {phase === "countdown"
              ? (() => {
                  const label = count >= 4 ? String(count) : count === 3 ? "Ready" : count === 2 ? "Set" : "Go";
                  const isWord = count <= 3;
                  return (
                    <div className="text-center py-6">
                      <div key={label} className={`font-heading ${isWord ? "text-6xl uppercase tracking-wide" : "text-7xl"} text-neutral-900 animate-error-pulse`} data-testid="mp-countdown">{label}</div>
                    </div>
                  );
                })()
              : (
                <div className="bg-neutral-100 -mx-3 px-3 pt-2 pb-3 border-t border-neutral-200">
                  <div className="flex items-end justify-between mb-1 px-1">
                    <span className="font-mono text-[11px] font-semibold text-neutral-700 tabular pb-1" data-testid="mp-cps-label"><Zap size={11} className="inline mb-0.5 mr-0.5" />{liveCps.toFixed(1)} CPS</span>
                    <Speedometer cps={liveCps} accurateStreak={streak} />
                    <span className="font-mono text-[11px] font-semibold text-neutral-500 tabular pb-1" data-testid="mp-pen-label">+{(mistakes * penaltySecs).toFixed(1)}S PEN</span>
                  </div>
                  <RacingKeyboard keys={race.keyboard} onPress={press} nextToken={null} disabled={phase !== "racing"} />
                </div>
              )}
          </div>
        </div>
      </div>
      {/* Real-time Racing Position Screen */}
      <div className="relative z-10 mx-3 mt-2"><RacePositions racers={racers} /></div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-race-surface border border-race-line rounded-md p-3">
    <div className="tabular text-2xl text-race-txt">{value}</div>
    <div className="text-[10px] text-race-sub uppercase tracking-widest mt-1">{label}</div>
  </div>
);
